/**
 * External dependencies
 */

import React from 'react';
import classnames from 'classnames';
import { sprintf, _n , __ } from '@wordpress/i18n';
import { speak } from '@wordpress/a11y';
// import { Label } from '@woocommerce/blocks-components';
import { Label } from '../../../../../packages/components';

// import ProductPrice from '@woocommerce/base-components/product-price';
import ProductPrice from '../../../../base/components/product-price';
// import ProductName from '@woocommerce/base-components/product-name';
import ProductName from '../../../../base/components/product-name';
// import {
// 	getCurrencyFromPriceResponse,
// 	formatPrice,
// } from '@woocommerce/price-format';

import { getCurrencyFromPriceResponse, formatPrice } from '../../../../../packages/prices';

// import { applyCheckoutFilter, mustContain } from '@woocommerce/blocks-checkout';
import { applyCheckoutFilter, mustContain } from '../../../../../packages/checkout'
import Dinero from 'dinero.js';

// import { getSetting } from '@woocommerce/settings';
import { getSetting } from '../../../../settings/shared';

import { useMemo } from '@wordpress/element';
// import { useStoreCart } from '@woocommerce/base-context/hooks';
import { useStoreCart, useStoreCartItemQuantity, useStoreEvents } from '../../../context/hooks';

// import { CartItem, isString } from '@woocommerce/types';

import { CartItem, isString } from '../../../../types';

/**
 * Internal dependencies
 */
import ProductBackorderBadge from '../product-backorder-badge';
import ProductImage from '../product-image';
import ProductLowStockBadge from '../product-low-stock-badge';
import ProductMetadata from '../product-metadata';

// import QuantitySelector from '@woocommerce/base-components/quantity-selector';
import QuantitySelector from '../../quantity-selector';

const productPriceValidation = ( value: string ): true | never =>
	mustContain( value, '<price/>' );

interface OrderSummaryProps {
	cartItem: CartItem;
	onRemove?: () => void;
}

const OrderSummaryItem = ( { cartItem, onRemove = () => void null }: OrderSummaryProps ): JSX.Element => {
	const {
		images,
		low_stock_remaining: lowStockRemaining,
		show_backorder_badge: showBackorderBadge,
		name: initialName,
		permalink,
		prices,
		quantity,
		short_description: shortDescription,
		description: fullDescription,
		item_data: itemData,
		variation,
		totals,
		extensions,


		/// @todo bhavik
		sold_individually: soldIndividually = false,
		quantity_limits: quantityLimits = {
			minimum: 1,
			maximum: 99,
			multiple_of: 1,
			editable: true,
		},
	} = cartItem;

	// Prepare props to pass to the applyCheckoutFilter filter.
	// We need to pluck out receiveCart.
	// eslint-disable-next-line no-unused-vars
	const { receiveCart, ...cart } = useStoreCart();

	const arg = useMemo(
		() => ( {
			context: 'summary',
			cartItem,
			cart,
		} ),
		[ cartItem, cart ]
	);

	const priceCurrency = getCurrencyFromPriceResponse( prices );

	const name = applyCheckoutFilter( {
		filterName: 'itemName',
		defaultValue: initialName,
		extensions,
		arg,
	} );

	const regularPriceSingle = Dinero( {
		amount: parseInt( prices.raw_prices.regular_price, 10 ),
		precision: isString( prices.raw_prices.precision )
			? parseInt( prices.raw_prices.precision, 10 )
			: prices.raw_prices.precision,
	} )
		.convertPrecision( priceCurrency.minorUnit )
		.getAmount();
	const priceSingle = Dinero( {
		amount: parseInt( prices.raw_prices.price, 10 ),
		precision: isString( prices.raw_prices.precision )
			? parseInt( prices.raw_prices.precision, 10 )
			: prices.raw_prices.precision,
	} )
		.convertPrecision( priceCurrency.minorUnit )
		.getAmount();
	const totalsCurrency = getCurrencyFromPriceResponse( totals );

	let lineSubtotal = parseInt( totals.line_subtotal, 10 );
	if ( getSetting( 'displayCartPricesIncludingTax', false ) ) {
		lineSubtotal += parseInt( totals.line_subtotal_tax, 10 );
	}
	const subtotalPrice = Dinero( {
		amount: lineSubtotal,
		precision: totalsCurrency.minorUnit,
	} ).getAmount();
	const subtotalPriceFormat = applyCheckoutFilter( {
		filterName: 'subtotalPriceFormat',
		defaultValue: '<price/>',
		extensions,
		arg,
		validation: productPriceValidation,
	} );

	// Allow extensions to filter how the price is displayed. Ie: prepending or appending some values.
	const productPriceFormat = applyCheckoutFilter( {
		filterName: 'cartItemPrice',
		defaultValue: '<price/>',
		extensions,
		arg,
		validation: productPriceValidation,
	} );

	const cartItemClassNameFilter = applyCheckoutFilter( {
		filterName: 'cartItemClass',
		defaultValue: '',
		extensions,
		arg,
	} );

	// @todo bhavik
	const { setItemQuantity, removeItem, isPendingDelete } = useStoreCartItemQuantity( cartItem );
	const { dispatchStoreEvent } = useStoreEvents();
	const showRemoveItemLink = applyCheckoutFilter( {
		filterName: 'showRemoveItemLink',
		defaultValue: true,
		extensions,
		arg,
	} );
	return (
		<div
			className={ classnames(
				'wc-block-components-order-summary-item',
				cartItemClassNameFilter
			) }
		>
			<div className="wc-block-components-order-summary-item__image">
				<div className="wc-block-components-order-summary-item__quantity">
					<Label
						label={ quantity.toString() }
						screenReaderLabel={ sprintf(
							/* translators: %d number of products of the same type in the cart */
							_n(
								'%d item',
								'%d items',
								quantity,
								'woo-gutenberg-products-block'
							),
							quantity
						) }
					/>
				</div>
				<ProductImage
					image={ images.length ? images[ 0 ] : {} }
					fallbackAlt={ name }
				/>
			</div>
			<div className="wc-block-components-order-summary-item__description">
				<ProductName
					disabled={ true }
					name={ name }
					permalink={ permalink }
				/>
				<ProductPrice
					currency={ priceCurrency }
					price={ priceSingle }
					regularPrice={ regularPriceSingle }
					className="wc-block-components-order-summary-item__individual-prices"
					priceClassName="wc-block-components-order-summary-item__individual-price"
					regularPriceClassName="wc-block-components-order-summary-item__regular-individual-price"
					format={ subtotalPriceFormat }
				/>
				{ showBackorderBadge ? (
					<ProductBackorderBadge />
				) : (
					!! lowStockRemaining && (
						<ProductLowStockBadge
							lowStockRemaining={ lowStockRemaining }
						/>
					)
				) }
				<ProductMetadata
					shortDescription={ shortDescription }
					fullDescription={ fullDescription }
					itemData={ itemData }
					variation={ variation }
				/>
				<div className="wc-block-cart-item__quantity">
							{ ! soldIndividually &&
								!! quantityLimits.editable && (
									<QuantitySelector
										disabled={ isPendingDelete }
										quantity={ quantity }
										minimum={ quantityLimits.minimum }
										maximum={ quantityLimits.maximum }
										step={ quantityLimits.multiple_of }
										onChange={ ( newQuantity ) => {
											setItemQuantity( newQuantity );
											dispatchStoreEvent(
												'cart-set-item-quantity',
												{
													product: cartItem,
													quantity: newQuantity,
												}
											);
										} }
										itemName={ name }
									/>
								) }
							{ showRemoveItemLink && (
								<button
									className="wc-block-cart-item__remove-link"
									aria-label={ sprintf(
										/* translators: %s refers to the item's name in the cart. */
										__(
											'Remove %s from cart',
											'woo-gutenberg-products-block'
										),
										name
									) }
									onClick={ () => {
										onRemove();
										removeItem();
										dispatchStoreEvent(
											'cart-remove-item',
											{
												product: cartItem,
												quantity,
											}
										);
										speak(
											sprintf(
												/* translators: %s refers to the item name in the cart. */
												__(
													'%s has been removed from your cart.',
													'woo-gutenberg-products-block'
												),
												name
											)
										);
									} }
									disabled={ isPendingDelete }
								>
									{ __(
										'Remove item',
										'woo-gutenberg-products-block'
									) }
								</button>
							) }
						</div> {/* end of quantity div */}
			</div>
			<span className="screen-reader-text">
				{ sprintf(
					/* translators: %1$d is the number of items, %2$s is the item name and %3$s is the total price including the currency symbol. */
					_n(
						'Total price for %1$d %2$s item: %3$s',
						'Total price for %1$d %2$s items: %3$s',
						quantity,
						'woo-gutenberg-products-block'
					),
					quantity,
					name,
					formatPrice( subtotalPrice, totalsCurrency )
				) }
			</span>
			<div
				className="wc-block-components-order-summary-item__total-price"
				aria-hidden="true"
			>
				<ProductPrice
					currency={ totalsCurrency }
					format={ productPriceFormat }
					price={ subtotalPrice }
				/>
			</div>
		</div>
	);
};

export default OrderSummaryItem;
